import { useCallback, useEffect, useState } from 'react';

import type { PricePackBubbleRow } from 'hooks/ppa/useFetchPricePackArchitecture';

export type PackArchitectureChartTableSelection = {
    segmentKey: string;
    columnKey?: string;
    bubbleKey?: string;
};

export function usePackArchitectureSelection(rows: PricePackBubbleRow[]) {
    const [selection, setSelection] = useState<PackArchitectureChartTableSelection | null>(null);

    useEffect(() => {
        setSelection((previous) => {
            if (!previous?.segmentKey) return null;

            const selectionStillExists = rows.some((row) => {
                const rowKey = row.ex_id || row.description || '';
                if (rowKey === previous.segmentKey) return true;
                return (row.data ?? []).some((axisItem) =>
                    (axisItem.data ?? []).some(
                        (segment) =>
                            (segment.ex_id || segment.description || '') === previous.segmentKey,
                    ),
                );
            });

            return selectionStillExists ? previous : null;
        });
    }, [rows]);

    const toggleSegment = useCallback((segmentKey: string) => {
        setSelection((previous) =>
            previous?.segmentKey === segmentKey && !previous.columnKey ? null : { segmentKey },
        );
    }, []);

    const selectSegment = useCallback((segmentKey: string) => {
        setSelection((previous) =>
            previous?.segmentKey === segmentKey && !previous.columnKey ? null : { segmentKey },
        );
    }, []);

    const selectBubble = useCallback(
        (payload: { segmentKey: string; columnKey: string; bubbleKey: string }) => {
            setSelection((previous) => (previous?.bubbleKey === payload.bubbleKey ? null : payload));
        },
        [],
    );

    const selectTableCell = useCallback((segmentKey: string, columnKey: string) => {
        setSelection((previous) =>
            previous?.segmentKey === segmentKey && previous?.columnKey === columnKey
                ? null
                : { segmentKey, columnKey },
        );
    }, []);

    const clearSelection = useCallback(() => setSelection(null), []);

    return {
        selection,
        activeSegmentKey: selection?.segmentKey ?? null,
        selectedColumnKey: selection?.columnKey ?? null,
        selectedBubbleKey: selection?.bubbleKey ?? null,
        toggleSegment,
        selectSegment,
        selectBubble,
        selectTableCell,
        clearSelection,
    };
}
